import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { paymentClient } from '@/api/grpc';
import { useAuthedRpc } from '@/hooks/common';
import { makeUserPath, makePaymentMethodPath } from '@/lib/proto';
import { create } from '@bufbuild/protobuf';
import { 
  PaymentMethod,
  PaymentMethodPath,
  UserPath
} from '@generated/proto/model';
import {
  CreatePaymentMethodRequestSchema,
  GetPaymentMethodRequestSchema,
  ListPaymentMethodsRequestSchema,
  DeletePaymentMethodRequestSchema
} from '@generated/proto/service';

// Create payment method
export function useCreatePaymentMethod() {
  const { run, loading, error } = useAuthedRpc(
    async (
      user,
      stripeId: string,
      setAsDefault?: boolean
    ) => {
      if (!stripeId?.trim()) throw new Error("stripeId is required.");

      console.log('🎯 Creating payment method with:', { userId: user.uid, stripeId, setAsDefault });

      const request = create(CreatePaymentMethodRequestSchema, {
        userPath: makeUserPath(user.uid),
        stripeId,
        setAsDefault: setAsDefault ?? false,
      });

      console.log('📤 Sending payment method request:', request);
      const response = await paymentClient.createPaymentMethod(request as any);
      console.log('📥 Payment method response:', response);

      return response;
    }
  );

  return { createPaymentMethod: run, loading, error };
}

// Get specific payment method
export function useGetPaymentMethod(paymentMethodPath?: PaymentMethodPath) {
  return useQuery({
    queryKey: ['paymentMethod', paymentMethodPath?.userId, paymentMethodPath?.paymentMethodId],
    queryFn: async () => {
      if (!paymentMethodPath) return null;
      
      const request = { paymentMethodPath };
      const response = await paymentClient.getPaymentMethod(request);
      return response.paymentMethod;
    },
    enabled: !!paymentMethodPath,
  });
}

// List payment methods for a user
export function useListPaymentMethods(userPath?: UserPath, type?: string) {
  return useQuery({
    queryKey: ['paymentMethods', userPath?.userId, type],
    queryFn: async () => {
      console.log('📋 Listing payment methods for:', { userPath, type });
      
      if (!userPath) {
        console.log('⚠️ No userPath provided, returning empty array');
        return [];
      }
      
      const request = create(ListPaymentMethodsRequestSchema, {
        userPath,
        type,
      });
      
      console.log('📤 Sending list payment methods request:', request);
      const response = await paymentClient.listPaymentMethods(request as any);
      console.log('📥 List payment methods response:', response);
      
      const paymentMethods = response.paymentMethods || [];
      console.log('📋 Found payment methods:', paymentMethods.length, paymentMethods);
      
      return paymentMethods;
    },
    enabled: !!userPath,
  });
}

// Delete payment method
export function useDeletePaymentMethod() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (paymentMethodPath: PaymentMethodPath) => {
      const request = { paymentMethodPath };
      await paymentClient.deletePaymentMethod(request);
    },
    onSuccess: (_, paymentMethodPath) => {
      // Invalidate payment methods list
      queryClient.invalidateQueries({ 
        queryKey: ['paymentMethods', paymentMethodPath.userId] 
      });
      // Remove specific payment method from cache
      queryClient.removeQueries({ 
        queryKey: ['paymentMethod', paymentMethodPath.userId, paymentMethodPath.paymentMethodId] 
      });
    },
  });
}